import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import './GameHistoryPage.css';

interface Game {
  id: string;
  game_type: string;
  status: string;
  created_at: string;
  completed_at?: string;
}

export function GameHistoryPage() {
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');

  useEffect(() => {
    loadGames();
  }, [filter]);

  const loadGames = async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await api.get('/games', { params });
      setGames(response.data);
    } catch (error) {
      console.error('Failed to load games:', error);
      alert('Ошибка загрузки истории');
    } finally {
      setLoading(false);
    }
  };

  const getGameTypeName = (type: string) => {
    const names: Record<string, string> = {
      base: 'Базовый',
      plates: 'Номера',
      roulette: 'Рулетка',
    };
    return names[type] || type;
  };

  const handleGameClick = (game: Game) => {
    navigate(`/games/${game.game_type}/${game.id}`);
  };

  const handleDeactivate = async (e: React.MouseEvent, gameId: string) => {
    e.stopPropagation(); // Prevent card click
    
    if (!confirm('Вы уверены, что хотите деактивировать эту игру?')) {
      return;
    }

    try {
      await api.patch(`/games/${gameId}/deactivate`);
      // Reload games list
      loadGames();
      // Also clear last-game if it was this game
      const lastGame = localStorage.getItem('last-game');
      if (lastGame) {
        try {
          const { gameId: lastGameId } = JSON.parse(lastGame);
          if (lastGameId === gameId) {
            localStorage.removeItem('last-game');
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    } catch (error: any) {
      console.error('Failed to deactivate game:', error);
      alert(error.response?.data?.error?.message || 'Ошибка деактивации игры');
    }
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="game-history-page">
      <div className="page-header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Назад
        </button>
        <h1>История игр</h1>
      </div>

      <div className="filters">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          Все
        </button>
        <button
          className={filter === 'active' ? 'active' : ''}
          onClick={() => setFilter('active')}
        >
          Активные
        </button>
        <button
          className={filter === 'completed' ? 'active' : ''}
          onClick={() => setFilter('completed')}
        >
          Завершённые
        </button>
      </div>

      <div className="games-list">
        {games.length === 0 ? (
          <div className="empty-state">Нет игр</div>
        ) : (
          games.map((game) => (
            <div
              key={game.id}
              className="game-card"
              onClick={() => handleGameClick(game)}
            >
              <div className="game-card-header">
                <h3>{getGameTypeName(game.game_type)}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className={`status-badge ${game.status}`}>
                    {game.status === 'active' ? 'Активна' : 'Завершена'}
                  </span>
                  {game.status === 'active' && (
                    <button
                      className="deactivate-button"
                      onClick={(e) => handleDeactivate(e, game.id)}
                      title="Деактивировать игру"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="game-card-info">
                <div>
                  <strong>Создана:</strong>{' '}
                  {new Date(game.created_at).toLocaleString('ru-RU')}
                </div>
                {game.completed_at && (
                  <div>
                    <strong>Завершена:</strong>{' '}
                    {new Date(game.completed_at).toLocaleString('ru-RU')}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

